// ---------------------------------------------------------------------------
// The room's deadline, read once.
//
// Split out of RoomExpiry.tsx — which is where the whole design of the header
// control and the warning strip is written down, and worth reading first — for
// one reason: the room owns exactly ONE of these and hands the reading to both
// pieces. A hook exported beside two components is also a fast-refresh warning,
// and this file has no component in it.
// ---------------------------------------------------------------------------

import { useEffect, useState } from 'react';
import { serverNow } from '../../store/gameStore.js';

/** Below this, the header button starts showing the number, in accent. */
export const WARN_MS = 10 * 60_000;
/** Below this, the strip appears and everything turns alert. */
export const URGENT_MS = 2 * 60_000;

/**
 * How long is left, said three ways.
 *
 * These live beside the clock rather than in RoomExpiry.tsx for the reason that
 * file was split in the first place: a function exported next to a component is
 * a fast-refresh warning, and there are now three surfaces reading the same
 * deadline — the header's room button, the panel behind it and the last-stretch
 * strip. One implementation is what stops them disagreeing about whether a room
 * has `9m` or `10m` left. The design of what is shown at each level is written
 * down in RoomExpiry.tsx's header; this is only the arithmetic.
 */
export type ExpiryLevel = 'none' | 'calm' | 'soon' | 'urgent';

export function levelFor(msLeft: number | null): ExpiryLevel {
  if (msLeft === null) return 'none';
  if (msLeft <= URGENT_MS) return 'urgent';
  if (msLeft <= WARN_MS) return 'soon';
  return 'calm';
}

/**
 * "1h58" with an hour or more to go, "9m" while there are minutes, "0:42" once
 * there are not.
 *
 * Three grains rather than one, because the useful reading changes as the
 * deadline approaches: an hour out nobody cares about the seconds, and inside
 * two minutes the seconds are the entire point.
 */
export function formatLeft(msLeft: number): string {
  const seconds = Math.ceil(msLeft / 1000);
  if (msLeft > WARN_MS) {
    const minutes = Math.ceil(seconds / 60);
    const hours = Math.floor(minutes / 60);
    return hours > 0 ? `${hours}h${String(minutes % 60).padStart(2, '0')}` : `${minutes}m`;
  }
  if (seconds >= 60 && msLeft > URGENT_MS) return `${Math.ceil(seconds / 60)}m`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, '0')}`;
}

/** The same reading, said in words, for a screen reader and for the label. */
export function spellLeft(msLeft: number): string {
  const seconds = Math.ceil(msLeft / 1000);
  if (seconds >= 3600) {
    const minutes = Math.ceil(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    const hourWord = hours === 1 ? '1 hour' : `${hours} hours`;
    return rest === 0 ? hourWord : `${hourWord} ${rest} minutes`;
  }
  if (seconds >= 120) return `${Math.ceil(seconds / 60)} minutes`;
  if (seconds === 60) return 'a minute';
  if (seconds > 60) return `${seconds} seconds`;
  return seconds === 1 ? '1 second' : `${seconds} seconds`;
}

/** One reading of the room's deadline, rendered by both pieces. */
export interface RoomExpiry {
  /** Milliseconds left, or null when nothing is counting. */
  msLeft: number | null;
  /** The deadline it was read against, which is what a dismissal is keyed on. */
  expiresAt: number | null;
}

/**
 * Milliseconds left, repainting only as often as the number on screen changes.
 *
 * A one-second interval running for the whole two hours would wake a phone
 * seven thousand times to redraw a pill with no number on it. So while the
 * deadline is far away this sleeps until the exact moment the warning is due,
 * and only then does it tick every second. The effect re-runs when the deadline
 * moves, which is what makes somebody else's press land here immediately.
 *
 * It returns a reading taken during render rather than one mirrored into state:
 * the timer exists to force the repaint, not to own the time. Mirroring it would
 * leave the last second of the countdown a frame behind, and would be a setState
 * in an effect, which this codebase treats as an error.
 *
 * **The room calls this once**, in RoomShell, and hands the result to the header
 * control and to the warning strip. They used to call it each, on the same
 * deadline, so two self-rescheduling chains ticked a second apart through the
 * last ten minutes of every room — and one of them was driving a component that
 * renders nothing at all until the last two.
 */
export function useRoomExpiry(expiresAt: number | null | undefined): RoomExpiry {
  const deadline = expiresAt ?? null;
  const [, repaint] = useState(0);

  useEffect(() => {
    if (deadline === null) return;
    let timer: ReturnType<typeof setTimeout>;
    const schedule = () => {
      const left = deadline - serverNow();
      // A minute while the reading is in minutes, a second once it is in
      // seconds. Two hours of this is a hundred and twenty repaints rather than
      // seven thousand, and the number on screen is never more than a minute
      // stale — which is the resolution it is being shown at.
      const delay = left > WARN_MS ? Math.min(60_000, Math.max(1_000, left - WARN_MS)) : 1_000;
      timer = setTimeout(() => {
        repaint((count) => count + 1);
        schedule();
      }, delay);
    };
    schedule();
    return () => clearTimeout(timer);
  }, [deadline]);

  return {
    msLeft: deadline === null ? null : Math.max(0, deadline - serverNow()),
    expiresAt: deadline,
  };
}
